export const ANALYTICS_WIDGET_SIZE_OPTIONS = [
  { label: 'Compact', width: 3, height: 1, className: 'col-span-12 sm:col-span-6 xl:col-span-3' },
  { label: 'Medium', width: 4, height: 1, className: 'col-span-12 md:col-span-6 xl:col-span-4' },
  { label: 'Half', width: 6, height: 2, className: 'col-span-12 xl:col-span-6' },
  { label: 'Wide', width: 8, height: 2, className: 'col-span-12 xl:col-span-8' },
  { label: 'Full', width: 12, height: 2, className: 'col-span-12' },
] as const;

export type AnalyticsWidgetCategory = 'Revenue' | 'Channels' | 'Audience' | 'Email quality';

export const DEFAULT_ANALYTICS_WIDGETS = [
  { id: 'kpi:attributed-revenue', title: 'Attributed revenue', category: 'Revenue', defaultWidth: 3, defaultHeight: 1 },
  { id: 'kpi:attributed-orders', title: 'Attributed orders', category: 'Revenue', defaultWidth: 3, defaultHeight: 1 },
  { id: 'kpi:revenue-per-contact', title: 'Revenue per contact', category: 'Revenue', defaultWidth: 3, defaultHeight: 1 },
  { id: 'kpi:reachable-audience', title: 'Reachable audience', category: 'Audience', defaultWidth: 3, defaultHeight: 1 },
  { id: 'chart:attributed-revenue-over-time', title: 'Attributed revenue over time', category: 'Revenue', defaultWidth: 8, defaultHeight: 2 },
  { id: 'chart:revenue-contribution', title: 'Revenue contribution', category: 'Revenue', defaultWidth: 4, defaultHeight: 2 },
  { id: 'card:email-health', title: 'Email health', category: 'Channels', defaultWidth: 4, defaultHeight: 1 },
  { id: 'card:push-health', title: 'Push health', category: 'Channels', defaultWidth: 4, defaultHeight: 1 },
  { id: 'card:whatsapp-health', title: 'WhatsApp health', category: 'Channels', defaultWidth: 4, defaultHeight: 1 },
  { id: 'chart:audience-net-growth', title: 'Audience net growth', category: 'Audience', defaultWidth: 6, defaultHeight: 2 },
  { id: 'chart:email-performance-trend', title: 'Email performance trend', category: 'Email quality', defaultWidth: 6, defaultHeight: 2 },
  { id: 'table:top-revenue-campaigns', title: 'Top revenue campaigns', category: 'Revenue', defaultWidth: 6, defaultHeight: 2 },
  { id: 'table:automation-performance', title: 'Automation performance', category: 'Revenue', defaultWidth: 6, defaultHeight: 2 },
  { id: 'kpi:email-delivery', title: 'Email delivery', category: 'Email quality', defaultWidth: 3, defaultHeight: 1 },
  { id: 'kpi:unique-click-signal', title: 'Unique click signal', category: 'Email quality', defaultWidth: 3, defaultHeight: 1 },
  { id: 'kpi:unsubscribe-rate', title: 'Unsubscribe rate', category: 'Email quality', defaultWidth: 3, defaultHeight: 1 },
  { id: 'kpi:spam-complaint-rate', title: 'Spam complaint rate', category: 'Email quality', defaultWidth: 3, defaultHeight: 1 },
] as const;

export const DEFAULT_ANALYTICS_WIDGET_IDS = DEFAULT_ANALYTICS_WIDGETS.map((widget) => widget.id);
export const ANALYTICS_WIDGET_ID_SET = new Set<string>(DEFAULT_ANALYTICS_WIDGET_IDS);

export type AnalyticsWidgetId = typeof DEFAULT_ANALYTICS_WIDGETS[number]['id'];
export type AnalyticsWidget = typeof DEFAULT_ANALYTICS_WIDGETS[number];

export interface ResolvedAnalyticsWidget {
  id: AnalyticsWidgetId;
  title: string;
  category: AnalyticsWidgetCategory;
  width: number;
  height: number;
  className: string;
}

export function getAnalyticsWidgetDefinition(id: AnalyticsWidgetId): AnalyticsWidget {
  const widget = DEFAULT_ANALYTICS_WIDGETS.find((item) => item.id === id);
  if (!widget) throw new Error(`Unknown analytics widget '${id}'`);
  return widget;
}

export function analyticsWidgetClassName(width: number): string {
  return ANALYTICS_WIDGET_SIZE_OPTIONS.find((option) => option.width === width)?.className ?? 'col-span-12';
}

export function normalizeAnalyticsWidgetWidth(id: AnalyticsWidgetId, width: number | null | undefined): number {
  if (width && ANALYTICS_WIDGET_SIZE_OPTIONS.some((option) => option.width === width)) return width;
  return getAnalyticsWidgetDefinition(id).defaultWidth;
}

export function normalizeAnalyticsWidgetHeight(id: AnalyticsWidgetId, height: number | null | undefined): number {
  if (height && height > 0) return height;
  return getAnalyticsWidgetDefinition(id).defaultHeight;
}
